import React, { useState } from 'react';
import { Lock, Unlock, DollarSign } from '../lib/icons';

const BusinessPricing = () => {
  const [accessCode, setAccessCode] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [error, setError] = useState('');

  // Access code - you can change this to whatever you want
  const CORRECT_CODE = 'BULK2024';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (accessCode.toUpperCase() === CORRECT_CODE) {
      setIsUnlocked(true);
      setError('');
    } else {
      setError('Invalid access code. Please contact us for business pricing access.');
      setAccessCode('');
    }
  };

  const pricingData = [
    {
      category: 'iPad Repairs',
      items: [
        { model: 'iPad 5-9', service: 'Charging Port', price: '$60' },
        { model: 'iPad 10/11', service: 'Charging Port', price: '$70' },
        { model: 'iPad Air 4/5', service: 'Charging Port', price: '$65' },
        { model: 'iPad (All Models)', service: 'Battery Connector', price: '$60' },
        { model: 'iPad Pro', service: 'CD3217', price: '$90' },
        { model: 'iPad', service: 'Tristar/Hydra', price: '$60' },
      ]
    },
    {
      category: 'Gaming Console Repairs',
      items: [
        { model: 'PS5/Xbox', service: 'HDMI Port (Same Day)', price: '$75' },
        { model: 'PS5/Xbox', service: 'HDMI Port', price: '$50' },
        { model: 'PS5', service: 'Ripped Fan Connector', price: '$75' },
        { model: 'PS5/Xbox', service: 'Ripped Trace Repair', price: 'Call' },
        { model: 'Nintendo Switch LCD/OLED', service: 'USB-C Port', price: '$60' },
        { model: 'Nintendo Switch LCD/OLED', service: 'Connector Replacement', price: 'Call' },
        { model: 'Switch 2', service: 'USB-C Port Replacement', price: 'Call' },
        { model: 'Nintendo Switch', service: 'No Power M92 or BQ', price: '$60' },
        { model: 'Nintendo Switch LCD/OLED', service: "Won't Dock", price: '$60' },
      ]
    }
  ];

  if (!isUnlocked) {
    return (
      <section id="business-pricing" className="py-20 bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="bg-[#39FF14]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-[#39FF14]" />
            </div>
            <h2 className="text-4xl font-bold text-[#E0E0E0] mb-4">Business & Bulk Pricing</h2>
            <p className="text-xl text-[#B0B0B0] max-w-2xl mx-auto">
              Exclusive pricing for repair shops, businesses, and bulk orders. Enter your access code to view pricing.
            </p>
          </div>

          <div className="bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-800 max-w-md mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="accessCode" className="block text-sm font-medium text-[#E0E0E0] mb-2">
                  Access Code
                </label>
                <input
                  type="text"
                  id="accessCode"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter your code"
                  className="w-full px-4 py-3 bg-[#0D0D0D] border border-gray-700 rounded-lg text-[#E0E0E0] placeholder-gray-500 focus:outline-none focus:border-[#39FF14] transition-colors"
                  required
                />
                {error && (
                  <p className="mt-2 text-sm text-[#FF1744]">{error}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-[#39FF14] text-[#0D0D0D] py-3 rounded-lg font-semibold hover:bg-[#00BFFF] hover:text-white transition-colors duration-200"
              >
                Access Pricing
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-800 text-center">
              <p className="text-sm text-[#B0B0B0] mb-2">
                Don't have an access code?
              </p>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-[#00BFFF] hover:text-[#39FF14] transition-colors font-medium"
              >
                Contact us for business inquiries
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="business-pricing" className="py-20 bg-[#0D0D0D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="bg-[#39FF14]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Unlock className="h-8 w-8 text-[#39FF14]" />
          </div>
          <h2 className="text-4xl font-bold text-[#E0E0E0] mb-4">Business & Bulk Pricing</h2>
          <p className="text-xl text-[#B0B0B0] max-w-2xl mx-auto mb-4">
            Exclusive pricing for repair shops and bulk orders
          </p>
          <p className="text-sm text-[#00BFFF]">
            * Volume discounts available for orders of 10+ units
          </p>
        </div>

        <div className="space-y-8">
          {pricingData.map((category, idx) => (
            <div key={idx} className="bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-800">
              <div className="flex items-center space-x-3 mb-6">
                <DollarSign className="h-6 w-6 text-[#39FF14]" />
                <h3 className="text-2xl font-bold text-[#E0E0E0]">{category.category}</h3>
              </div>

              {category.category === 'iPad Repairs' && (
                <div className="mb-6 bg-[#00BFFF]/10 border border-[#00BFFF]/30 rounded-lg p-4">
                  <p className="text-sm text-[#E0E0E0] leading-relaxed">
                    <span className="font-semibold text-[#00BFFF]">Note:</span> Pricing below is with you handling the disassembly and reassembly. You hand me the board and I solder on the part. If you would rather I handle the repair start to finish, call for pricing.
                  </p>
                </div>
              )}

              {category.category === 'Gaming Console Repairs' && (
                <div className="mb-6 bg-[#00BFFF]/10 border border-[#00BFFF]/30 rounded-lg p-4">
                  <p className="text-sm text-[#E0E0E0] leading-relaxed">
                    <span className="font-semibold text-[#00BFFF]">Note:</span> Unless stated, repair turnaround is 3-5 days. Pricing reflects board only.
                  </p>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-[#B0B0B0] font-medium">Model</th>
                      <th className="text-left py-3 px-4 text-[#B0B0B0] font-medium">Service</th>
                      <th className="text-right py-3 px-4 text-[#B0B0B0] font-medium">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.items.map((item, itemIdx) => (
                      <tr key={itemIdx} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                        <td className="py-3 px-4 text-[#E0E0E0]">{item.model}</td>
                        <td className="py-3 px-4 text-[#B0B0B0]">{item.service}</td>
                        <td className="py-3 px-4 text-right text-[#39FF14] font-semibold">{item.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {category.category === 'Gaming Console Repairs' && (
                <div className="mt-6 bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  <p className="text-sm text-[#B0B0B0] leading-relaxed mb-3">
                    When repairing HDMI ports, we use a donor housing and power supply to power on your console just long enough to confirm the image is displaying correctly. We also take a quick photo of your gamer profile and serial number, which we attach to your ticket for your peace of mind.
                  </p>
                  <p className="text-sm text-[#B0B0B0] leading-relaxed">
                    For Nintendo Switch repairs, I need the screen and housing to properly test charging and overall functionality. It's not uncommon for Switch batteries to become completely depleted, requiring a quick jump from a power supply to bring them back above 3 volts. When that happens, I'll revive the battery at no extra charge.
                  </p>
                </div>
              )}

              {category.category === 'iPad Repairs' && (
                <div className="mt-6 bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  <p className="text-sm text-[#B0B0B0] leading-relaxed">
                    For iPad repairs, it's helpful—but not required—to include the housing and screen. Whenever possible, I prefer to reinstall the logic board into its original housing to ensure proper charging and functionality during testing.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#39FF14]/20 to-[#00BFFF]/20 rounded-2xl p-8 border border-[#39FF14]/30">
          <h3 className="text-2xl font-bold text-[#E0E0E0] mb-4 text-center">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[#B0B0B0]">
            <div>
              <h4 className="font-semibold text-[#E0E0E0] mb-2">Volume Discounts:</h4>
              <ul className="space-y-1 text-sm">
                <li>• 10-24 units: 10% off</li>
                <li>• 25-49 units: 15% off</li>
                <li>• 50+ units: 20% off</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#E0E0E0] mb-2">Terms:</h4>
              <ul className="space-y-1 text-sm">
                <li>• All prices include parts and labor</li>
                <li>• 90-day warranty on all repairs</li>
                <li>• Same-day service available</li>
                <li>• Custom pricing for special projects</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#FF1744] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#39FF14] hover:text-[#0D0D0D] transition-colors duration-200"
          >
            Request Custom Quote
          </button>
        </div>
      </div>
    </section>
  );
};

export default BusinessPricing;
